<template>
    <Modal2 :visible="true" @cancel="handleCancel" :showTitle="false" :showFooter="false" :large="true"
        className="ai-assistant-modal">
        <div class="mail-ai-assistant">
            <!-- Loading Overlay -->
            <div class="loading-overlay" v-if="isLoading">
                <div class="loading-container">
                    <div class="ai-brain">
                        <i class="material-icons animate-pulse">psychology</i>
                    </div>
                    <div class="loading-text">{{ $t("mail.mailAiAssistant.loadingText") }}</div>
                    <div class="loading-description">{{ $t("mail.mailAiAssistant.loadingDescription") }}</div>
                    <div class="loading-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            </div>

            <!-- Ana Menü -->
            <div class="assistant-dashboard" v-if="mode === 'menu' && !showSentimentResult && !showPreviewResult && !showSubOptions && !showCustomPromptScreen">
                <div class="ai-assistant-header">
                    <div class="ai-logo">
                        <i class="material-icons">psychology</i>
                    </div>
                    <div class="ai-title-container">
                        <h2>{{ $t("mail.mailAiAssistant.aiTitle") }}</h2>
                        <p class="ai-description">{{ $t("mail.mailAiAssistant.aiDescription") }}
                        </p>
                    </div>
                </div>

                <div class="feature-cards">
                    <div v-if="!hasContent && filteredMenuItems.length === 0" class="empty-state">
                        <div class="empty-icon">
                            <i class="material-icons">email_off</i>
                        </div>
                        <h3>{{ $t("mail.mailAiAssistant.emptyContentTitle") }}</h3>
                        <p>{{ $t("mail.mailAiAssistant.emptyContentDescription") }}</p>
                    </div>

                    <div v-for="(item, index) in filteredMenuItems" :key="index" class="feature-card"
                        @click="handleOptionClick(item)">
                        <div class="card-icon">
                            <i class="material-icons">{{ item.icon }}</i>
                        </div>
                        <div class="card-content">
                            <h3>{{ item.title }}</h3>
                            <p v-if="item.description">{{ item.description }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Alt Menü Seçenekleri -->
            <div class="sub-menu-screen" v-if="showSubOptions && !showSentimentResult && !showPreviewResult && !showCustomPromptScreen">
                <div class="sub-menu-header">
                    <button class="back-button" @click="showSubOptions = false">
                        <i class="material-icons">arrow_back</i>
                        <span>{{ $t("message.back") }}</span>
                    </button>
                    <h3>{{ currentOptionTitle }}</h3>
                </div>
                <div class="sub-options-container">
                    <div v-if="!hasContent && filteredSubOptions.length === 0" class="empty-state">
                        <div class="empty-icon">
                            <i class="material-icons">email_off</i>
                        </div>
                        <h3>{{ $t("mail.mailAiAssistant.emptyContentTitle") }}</h3>
                        <p>{{ $t("mail.mailAiAssistant.emptyContentDescription") }}</p>
                    </div>

                    <div v-else-if="!hasContent && filteredSubOptions.length > 0" class="limited-access-banner">
                        <i class="material-icons">info</i>
                        <p>{{ $t("mail.mailAiAssistant.limitedAccessDescription") }}</p>
                    </div>

                    <div v-for="(option, index) in filteredSubOptions" :key="index" class="sub-option-card"
                        @click="handleSubOptionClick(option)">
                        <div class="option-icon" v-if="option.icon">
                            <i class="material-icons">{{ option.icon }}</i>
                        </div>
                        <div class="option-content">
                            <h4>{{ option.title }}</h4>
                            <p v-if="option.description">{{ option.description }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Duygu Analizi Sonuç Ekranı -->
            <div class="results-screen" v-if="(showSentimentResult || mode === 'sentiment') && !showSubOptions && !showPreviewResult && !showCustomPromptScreen">
                <div class="results-container">
                    <div class="sentiment-summary">
                        <div class="sentiment-badge" :class="sentimentResult.tone.toLowerCase()">
                            <i class="material-icons">
                                {{
                                    sentimentResult.tone === 'Pozitif' ? 'sentiment_very_satisfied' :
                                        sentimentResult.tone === 'Negatif' ? 'sentiment_very_dissatisfied' :
                                            sentimentResult.tone === 'Karışık' ? 'sentiment_neutral' : 'sentiment_neutral'
                                }}
                            </i>
                            <span>{{ sentimentResult.tone }}</span>
                        </div>
                    </div>

                    <div class="result-section emotions-section">
                        <h4><i class="material-icons">psychology</i> {{ $t("mail.mailAiAssistant.resultEmotions") }}</h4>
                        <div class="emotions-tags">
                            <div v-for="(emotion, index) in sentimentResult.emotions" :key="index" class="emotion-tag">
                                {{ emotion }}
                            </div>
                        </div>
                    </div>

                    <div class="result-section explanation-section">
                        <h4><i class="material-icons">info</i> {{ $t("mail.mailAiAssistant.analysisDescription") }}</h4>
                        <p>{{ sentimentResult.explanation }}</p>
                    </div>

                    <div class="result-section recommendations-section"
                        v-if="sentimentResult.recommendations && sentimentResult.recommendations.length > 0">
                        <h4><i class="material-icons">lightbulb</i> {{ $t("mail.mailAiAssistant.recommendationsList") }}</h4>
                        <ul class="recommendations-list">
                            <li v-for="(recommendation, index) in sentimentResult.recommendations" :key="index">
                                {{ recommendation }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- Önizleme Ekranı (Özet dahil) -->
            <div class="preview-screen" v-if="(showPreviewResult || mode === 'summarize') && !showSubOptions && !showSentimentResult && !showCustomPromptScreen">
                <div class="preview-header">
                    <button class="back-button" @click="handlePreviewBack">
                        <i class="material-icons">{{ useCloseButton ? 'close' : 'arrow_back' }}</i>
                        <span>{{ useCloseButton ? $t('message.close') : $t('message.back') }}</span>
                    </button>
                    <h3>{{ previewTitle || (mode === 'summarize' ? $t('mail.mailAiAssistant.mailSummarize') : '') }}</h3>
                </div>
                <div class="preview-container">
                    <div class="ai-response-box">
                        <div class="response-header">
                            <i class="material-icons">smart_toy</i>
                            <span>{{ $t('mail.mailAiAssistant.aiResponse') }}</span>
                        </div>
                        <div class="response-content">
                            <div v-html="previewContent"></div>
                        </div>
                    </div>

                    <div class="action-buttons" v-if="!useCloseButton">
                        <button class="action-button regenerate" @click="regenerateContent">
                            <i class="material-icons">refresh</i>
                            <span>{{ $t('mail.mailAiAssistant.rebuild') }}</span>
                        </button>
                        <button class="action-button apply" @click="applyPreviewContent">
                            <i class="material-icons">check_circle</i>
                            <span>{{ $t('mail.mailAiAssistant.applyText') }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Özel İstek Ekranı -->
            <div class="custom-prompt-screen" v-if="showCustomPromptScreen && !showSubOptions && !showSentimentResult && !showPreviewResult">
                <div class="custom-prompt-header">
                    <button class="back-button" @click="showCustomPromptScreen = false">
                        <i class="material-icons">arrow_back</i>
                        <span>{{ $t("message.back") }}</span>
                    </button>
                    <h3>{{ $t('mail.mailAiAssistant.specialRequest') }}</h3>
                </div>
                <div class="custom-prompt-container">
                    <S2Textarea
                        v-model="customPrompt"
                        :placeholder="$t('mail.mailAiAssistant.specialRequestPlaceholder')"
                        :showAITool="false"
                    ></S2Textarea>
                    <button class="action-button apply" @click="handleCustomPrompt">
                        <i class="material-icons">check_circle</i>
                        <span>{{ $t('mail.mailAiAssistant.sendRequest') }}</span>
                    </button>
                </div>
            </div>
        </div>
    </Modal2>
</template>

<script>
import CoPilot from "../../../../../utils/CoPilot";
import { mapState } from "vuex";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import Modal2 from "../../../../../components/common/Modal2.vue";
import S2Textarea from "../../../../../components/S2Textarea.vue";

export default {
    name: "MailAIAssistant",
    components: { SupsisButton, Modal2, S2Textarea },
    props: {
        mailContent: {
            type: String,
            default: "",
        },
        mailDetails: {
            type: Object,
            default: () => ({}),
        },
        threadMails: {
            type: Array,
            default: () => [],
        },
        mode: {
            type: String,
            default: "menu",
            validator: (value) => ["menu", "sentiment", "summarize"].includes(value)
        },
        autoRun: {
            type: Boolean,
            default: false
        },
        useCloseButton: {
            type: Boolean,
            default: false
        },
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            language: (state) => state.language.language,
        }),
        currentOptionTitle() {
            const selectedItem = this.menuItems.find(item => item.hasSubOptions && this.showSubOptions && this.currentSubOptions === item.subOptions);
            return selectedItem ? selectedItem.title : '';
        },
        threadMailsContent() {
            // Thread içindeki maillerin içeriğini düz metin olarak birleştir
            if (!this.threadMails || this.threadMails.length === 0) return "";

            return this.threadMails.map(mail => {
                return `
                    Tarih: ${new Date(mail.createdAt).toLocaleString()}
                    Kimden: ${mail.sender}
                    Kime: ${mail.to_list.join(', ')}
                    Konu: ${mail.subject}

                    ${this.stripHtmlTags(mail.body_html || "")}
                    ---
                `;
            }).join("\n");
        },
        // İçerik var mı yok mu kontrol eden property
        hasContent() {
            return (this.mailContent && this.mailContent.trim() !== '') ||
                (this.threadMails && this.threadMails.length > 0);
        },
        // İçerik durumuna göre filtrelenmiş menü öğeleri
        filteredMenuItems() {
            if (this.hasContent) {
                // İçerik varsa tüm menü öğelerini göster
                return this.menuItems;
            } else {
                // İçerik yoksa sadece içerik gerektirmeyen öğeleri göster
                return this.menuItems.filter(item => !item.requiresContent);
            }
        },
        // Alt menüyü filtreleyen computed property
        filteredSubOptions() {
            if (this.hasContent) {
                // İçerik varsa tüm alt menü öğelerini göster
                return this.currentSubOptions;
            } else {
                // İçerik yoksa sadece içerik gerektirmeyen alt menü öğelerini göster
                return this.currentSubOptions.filter(option => !option.requiresContent);
            }
        }
    },
    data() {
        return {
            menuItems: [
                {
                    id: "fix-grammar",
                    title: this.$t("mail.mailAiAssistant.fixGrammarTitle"),
                    description: this.$t("mail.mailAiAssistant.fixGrammardescription"),
                    icon: "spellcheck",
                    action: this.fixGrammar,
                },
                {
                    id: "expand",
                    title: this.$t("mail.mailAiAssistant.expandTextTitle"),
                    description: this.$t("mail.mailAiAssistant.expandTextDescription"),
                    icon: "keyboard_double_arrow_down",
                    action: this.expandEmail,
                },
                {
                    id: "change-tone",
                    title: this.$t("mail.mailAiAssistant.changeToneTitle"),
                    description: this.$t("mail.mailAiAssistant.changeToneDescription"),
                    icon: "tune",
                    hasSubOptions: true,
                    subOptions: [
                        {
                            id: "professional",
                            title: this.$t("mail.mailAiAssistant.changeTone_ProfessionalTitle"),
                            description: this.$t("mail.mailAiAssistant.changeTone_ProfessionalDescription"),
                            icon: "business_center",
                            action: () => this.changeTone("professional"),
                        },
                        {
                            id: "friendly",
                            title: this.$t("mail.mailAiAssistant.changeTone_FriendlyTitle"),
                            description: this.$t("mail.mailAiAssistant.changeTone_FriendlyDescription"),
                            icon: "people",
                            action: () => this.changeTone("friendly"),
                        },
                        {
                            id: "casual",
                            title: this.$t("mail.mailAiAssistant.changeTone_CasualTitle"),
                            description: this.$t("mail.mailAiAssistant.changeTone_CasualDescription"),
                            icon: "chat",
                            action: () => this.changeTone("casual"),
                        },
                        {
                            id: "formal",
                            title: this.$t("mail.mailAiAssistant.changeTone_FormalTitle"),
                            description: this.$t("mail.mailAiAssistant.changeTone_FormalDescription"),
                            icon: "gavel",
                            action: () => this.changeTone("formal"),
                        },
                        {
                            id: "confident",
                            title: this.$t("mail.mailAiAssistant.changeTone_ConfidentTitle"),
                            description: this.$t("mail.mailAiAssistant.changeTone_ConfidentDescription"),
                            icon: "trending_up",
                            action: () => this.changeTone("confident"),
                        }
                    ]
                },
                {
                    id: "translate",
                    title: this.$t("mail.mailAiAssistant.translateTitle"),
                    description: this.$t("mail.mailAiAssistant.translateDescription"),
                    icon: "translate",
                    hasSubOptions: true,
                    subOptions: [
                        {
                            id: "tr",
                            title: "Türkçe",
                            icon: "language",
                            action: () => this.translateEmail("tr"),
                        },
                        {
                            id: "en",
                            title: "English",
                            icon: "language",
                            action: () => this.translateEmail("en"),
                        },
                        {
                            id: "de",
                            title: "Deutsch",
                            icon: "language",
                            action: () => this.translateEmail("de"),
                        },
                        {
                            id: "fr",
                            title: "Français",
                            icon: "language",
                            action: () => this.translateEmail("fr"),
                        },
                        {
                            id: "es",
                            title: "Español",
                            icon: "language",
                            action: () => this.translateEmail("es"),
                        },
                        {
                            id: "ar",
                            title: "العربية",
                            icon: "language",
                            action: () => this.translateEmail("ar"),
                        }
                    ]
                },
                {
                    id: "generate-reply",
                    title: this.$t("mail.mailAiAssistant.generateReplyTitle"),
                    description: this.$t("mail.mailAiAssistant.generateReplyDescription"),
                    icon: "autorenew",
                    action: this.generateReply,
                    requiresContent: true
                },
                {
                    id: "custom-prompt",
                    title: this.$t("mail.mailAiAssistant.customPromptTitle"),
                    description: this.$t("mail.mailAiAssistant.customPromptDescription"),
                    icon: "emoji_objects",
                    action: this.makeShowCustomPromptScreen,
                    requiresContent: false // Özel istek her zaman gösterilebilir
                }
            ],
            showSubOptions: false,
            currentSubOptions: [],
            copilot: null,
            showSentimentResult: false,
            sentimentResult: {
                tone: "",
                emotions: [],
                explanation: ""
            },
            showPreviewResult: false,
            previewTitle: "",
            previewContent: "",
            previewCurrentAction: null,
            previewActionParams: null,
            previewActionCallback: null,
            isLoading: false,
            showCustomPromptScreen: false,
            customPrompt: ""
        };
    },
    created() {
        this.copilot = CoPilot.getInstance();

        // Auto-run özelliği aktifse belirtilen modu hemen çalıştır
        if (this.autoRun) {
            this.$nextTick(() => {
                if (this.mode === 'sentiment') {
                    this.analyzeSentiment();
                } else if (this.mode === 'summarize') {
                    this.summarizeEmail();
                }
            });
        }
    },
    methods: {
        closeAssistant() {
            this.$emit("close");
        },

        handleCancel() {
            this.$emit("close");
        },

        handleOptionClick(item) {
            if (item.requiresContent && !this.hasContent) {
                this.$message.warning(this.$t("mail.mailAiAssistant.contentRequired"));
                return;
            }

            if (item.hasSubOptions && item.subOptions) {
                this.showSubOptions = true;
                this.currentSubOptions = item.subOptions;
            } else if (item.action) {
                item.action();
            }
        },

        handleSubOptionClick(option) {
            if (option.requiresContent && !this.hasContent) {
                this.$message.warning(this.$t("mail.mailAiAssistant.contentRequired"));
                return;
            }

            if (option.action) {
                option.action();
                this.showSubOptions = false;
            }
        },

        stripHtmlTags(input) {
            if (!input) return '';
            const div = document.createElement('div');
            div.innerHTML = input;
            return div.textContent || div.innerText || '';
        },

        // Editor içeriğinden yanıtlanmış/yönlendirilmiş kısımları temizleme (collapsed-mail içeriği)
        cleanMailContent(htmlContent) {
            if (!htmlContent) return '';

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlContent;

            // Yanıtlanan/yönlendirilen mesaj etiketlerini bul ve kaldır
            const collapsedElements = tempDiv.querySelectorAll('.collapsed-mail-wrapper, collapsed-mail');
            collapsedElements.forEach(element => {
                element.remove();
            });

            // Temizlenmiş içeriği al
            return this.stripHtmlTags(tempDiv.innerHTML);
        },

        // AI işlevleri
        async fixGrammar() {
            try {
                this.isLoading = true;
                const content = this.cleanMailContent(this.mailContent);
                const response = await this.copilot.fixGrammar(content);
                this.isLoading = false;

                this.showPreviewResult = true;
                this.previewTitle = this.$t("mail.fixGrammarTitle");
                this.previewContent = response;
                this.previewCurrentAction = "fixGrammar";
                this.previewActionParams = { content };
                this.previewActionCallback = () => {
                    this.$emit("update-content", response);
                    this.$message.success(this.$t("mail.mailAiAssistant.grammarFixed"));
                    this.closeAssistant();
                };
            } catch (error) {
                this.isLoading = false;
                console.error("AI işlevi hatası:", error);
                this.$message.error(this.$t("mail.mailAiAssistant.aiError"));
            }
        },

        async expandEmail() {
            try {
                this.isLoading = true;
                const content = this.cleanMailContent(this.mailContent);
                const response = await this.copilot.longSentence(content);
                this.isLoading = false;

                this.showPreviewResult = true;
                this.previewTitle = this.$t("mail.expandTextTitle");
                this.previewContent = response;
                this.previewCurrentAction = "expandEmail";
                this.previewActionParams = { content };
                this.previewActionCallback = () => {
                    this.$emit("update-content", response);
                    this.$message.success(this.$t("mail.mailAiAssistant.contentExpanded"));
                    this.closeAssistant();
                };
            } catch (error) {
                this.isLoading = false;
                console.error("AI işlevi hatası:", error);
                this.$message.error(this.$t("mail.mailAiAssistant.aiError"));
            }
        },

        async summarizeEmail() {
            try {
                this.isLoading = true;
                const content = this.threadMailsContent;
                const response = await this.copilot.summarizeContent(content);
                this.isLoading = false;

                this.previewTitle = this.$t("mail.summaryTitle");
                this.previewContent = response;
                this.previewCurrentAction = "summarizeEmail";
                this.previewActionParams = { content };
                this.previewActionCallback = () => {
                    this.$emit("update-content", response);
                    this.$message.success(this.$t("mail.mailAiAssistant.emailSummarized"));
                    this.closeAssistant();
                };

            } catch (error) {
                this.isLoading = false;
                console.error("AI işlevi hatası:", error);
                this.$message.error(this.$t("mail.mailAiAssistant.aiError"));
            }
        },

        async changeTone(tone) {
            try {
                this.isLoading = true;
                // Editör içeriğini temizle (collapsed-mail içeriği olmadan)
                const content = this.cleanMailContent(this.mailContent);
                const response = await this.copilot.changeTone(content, tone);
                this.isLoading = false;

                // Yanıtı göstermek için state değişkenleri
                this.showPreviewResult = true;
                this.previewTitle = this.$t("mail.changeToneTitle");
                this.previewContent = response;
                this.previewCurrentAction = "changeTone";
                this.previewActionParams = { content, tone };
                this.previewActionCallback = () => {
                    this.$emit("update-content", response);

                    let toneText = "değiştirildi";
                    if (tone === "professional") toneText = "profesyonel";
                    if (tone === "friendly") toneText = "arkadaşça";
                    if (tone === "casual") toneText = "günlük";
                    if (tone === "formal") toneText = "resmi";
                    if (tone === "confident") toneText = "özgüvenli";

                    this.$message.success(this.$t("mail.mailAiAssistant.toneChanged", { tone: toneText }));
                    this.closeAssistant();
                };
            } catch (error) {
                this.isLoading = false;
                console.error("AI işlevi hatası:", error);
                this.$message.error(this.$t("mail.mailAiAssistant.aiError"));
            }
        },

        async translateEmail(languageCode) {
            try {
                this.isLoading = true;
                // Editör içeriğini temizle (collapsed-mail içeriği olmadan)
                const content = this.cleanMailContent(this.mailContent);
                const response = await this.copilot.messageTranslation(content, languageCode);
                this.isLoading = false;

                // Yanıtı göstermek için state değişkenleri
                this.showPreviewResult = true;
                this.previewTitle = this.$t("mail.translationTitle");
                this.previewContent = response;
                this.previewCurrentAction = "translateEmail";
                this.previewActionParams = { content, languageCode };
                this.previewActionCallback = () => {
                    this.$emit("update-content", response);

                    let languageName = "";
                    if (languageCode === "tr") languageName = "Türkçe";
                    if (languageCode === "en") languageName = "İngilizce";
                    if (languageCode === "de") languageName = "Almanca";
                    if (languageCode === "fr") languageName = "Fransızca";
                    if (languageCode === "es") languageName = "İspanyolca";
                    if (languageCode === "ar") languageName = "Arapça";
                    this.$message.success(this.$t("mail.mailAiAssistant.translated", { language: languageName }));
                    this.closeAssistant();
                };
            } catch (error) {
                this.isLoading = false;
                console.error("AI işlevi hatası:", error);
                this.$message.error(this.$t("mail.mailAiAssistant.aiError"));
            }
        },

        async generateReply() {
            try {
                this.isLoading = true;

                const content = this.cleanMailContent(this.mailContent);

                const enrichedMailDetails = {
                    ...this.mailDetails,
                    content: content
                };

                const mailDetailsJson = JSON.stringify(enrichedMailDetails);

                const response = await this.copilot.mailAiresponse(
                    this.site._id,
                    this.user._id,
                    mailDetailsJson
                );

                this.isLoading = false;

                this.showPreviewResult = true;
                this.previewTitle = this.$t("mail.replyTitle");
                this.previewContent = response;
                this.previewCurrentAction = "generateReply";
                this.previewActionParams = { content };
                this.previewActionCallback = () => {
                    this.$emit("update-content", response);
                    this.$message.success(this.$t("mail.mailAiAssistant.replyGenerated"));
                    this.closeAssistant();
                };
            } catch (error) {
                this.isLoading = false;
                console.error("AI işlevi hatası:", error);
                this.$message.error(this.$t("mail.mailAiAssistant.aiError"));
            }
        },

        async analyzeSentiment() {
            try {
                this.isLoading = true;
                const content = this.threadMailsContent;
                const response = await this.copilot.analyzeSentiment(content);
                this.isLoading = false;

                this.sentimentResult = response;
                this.showSentimentResult = true;

            } catch (error) {
                this.isLoading = false;
                console.error("AI işlevi hatası:", error);
                this.$message.error(this.$t("mail.mailAiAssistant.aiError"));
            }
        },

        applyPreviewContent() {
            if (this.previewActionCallback) {
                this.previewActionCallback();
            }
        },

        async regenerateContent() {
            if (!this.previewCurrentAction) return;

            this.isLoading = true;

            try {
                let response;
                switch (this.previewCurrentAction) {
                    case "fixGrammar":
                        response = await this.copilot.fixGrammar(this.previewActionParams.content);
                        break;
                    case "expandEmail":
                        response = await this.copilot.longSentence(this.previewActionParams.content);
                        break;
                    case "summarizeEmail":
                        response = await this.copilot.summarizeContent(this.previewActionParams.content);
                        break;
                    case "changeTone":
                        response = await this.copilot.changeTone(
                            this.previewActionParams.content,
                            this.previewActionParams.tone
                        );
                        break;
                    case "translateEmail":
                        response = await this.copilot.messageTranslation(
                            this.previewActionParams.content,
                            this.previewActionParams.languageCode
                        );
                        break;
                    case "generateReply":
                        // Yapay zekaya gönderilecek verileri hazırla
                        const enrichedMailDetails = {
                            ...this.mailDetails,
                            content: this.previewActionParams.content || this.cleanMailContent(this.mailContent)
                        };

                        // JSON olarak dönüştürerek gönderelim
                        const mailDetailsJson = JSON.stringify(enrichedMailDetails);

                        response = await this.copilot.mailAiresponse(
                            this.site._id,
                            this.user._id,
                            mailDetailsJson
                        );
                        break;
                    case "customPrompt":
                        // Özel istek için
                        const promptMailDetails = {
                            ...this.mailDetails,
                            content: this.previewActionParams.content || this.cleanMailContent(this.mailContent)
                        };

                        // JSON olarak dönüştürerek gönderelim
                        const promptMailDetailsJson = JSON.stringify(promptMailDetails);

                        response = await this.copilot.mailPromtMessage(
                            this.site._id,
                            this.user._id,
                            promptMailDetailsJson,
                            this.previewActionParams.prompt
                        );
                        break;
                    default:
                        break;
                }

                this.isLoading = false;

                if (response) {
                    this.previewContent = response;
                    this.previewActionCallback = () => {
                        this.$emit("update-content", response);
                        this.$message.success(this.$t("mail.mailAiAssistant.contentUpdated"));
                        this.closeAssistant();
                    };
                    this.$message.success(this.$t("mail.mailAiAssistant.contentRebuilt"));
                }
            } catch (error) {
                this.isLoading = false;
                console.error("Yeniden oluşturma hatası:", error);
                this.$message.error(this.$t("mail.mailAiAssistant.contentRebuildFailed"));
            }
        },

        makeShowCustomPromptScreen() {
            this.showCustomPromptScreen = true;
        },

        handleCustomPrompt() {
            if (!this.customPrompt || this.customPrompt.trim() === '') {
                this.$message.error(this.$t("mail.mailAiAssistant.customRequestEmpty"));
                return;
            }

            this.executeCustomPrompt();
        },

        async executeCustomPrompt() {
            try {
                this.isLoading = true;
                this.showCustomPromptScreen = false;

                // İçeriği hazırla
                const content = this.cleanMailContent(this.mailContent);

                // Yapay zekaya gönderilecek verileri hazırla
                const enrichedMailDetails = {
                    ...this.mailDetails,
                    content: content // E-posta içeriği
                };

                // JSON olarak dönüştürerek gönderelim
                const mailDetailsJson = JSON.stringify(enrichedMailDetails);

                // Özel istek ile birlikte API'ye gönder
                const response = await this.copilot.mailPromtMessage(
                    this.site._id,
                    this.user._id,
                    mailDetailsJson,
                    this.customPrompt // Kullanıcının girdiği özel istek
                );

                this.isLoading = false;

                // Yanıtı göstermek için state değişkenleri
                this.showPreviewResult = true;
                this.previewTitle = this.$t("mail.customRequestTitle");
                this.previewContent = response;
                this.previewCurrentAction = "customPrompt";
                this.previewActionParams = {
                    content,
                    prompt: this.customPrompt
                };
                this.previewActionCallback = () => {
                    this.$emit("update-content", response);
                    this.$message.success(this.$t("mail.mailAiAssistant.customRequestApplied"));
                    this.closeAssistant();
                };

                // İstek tamamlandıktan sonra prompt'u temizle
                this.customPrompt = "";
            } catch (error) {
                this.isLoading = false;
                console.error("AI işlevi hatası:", error);
                this.$message.error(this.$t("mail.mailAiAssistant.aiError"));
            }
        },

        handlePreviewBack() {
            if (this.useCloseButton) {
                this.$emit("close");
                return;
            }

            this.showPreviewResult = false;

            if (this.mode !== 'menu' && this.autoRun) {
                this.$emit("close");
            }
        },

        handleBackFromResults(source) {
            if (this.useCloseButton) {
                // Kapat butonu kullanılıyorsa, bileşeni sonlandır
                this.$emit("close");
                return;
            }

            // Normal geri butonu kullanılıyorsa (ana menüye dön)
            if (source === 'sentiment') {
                this.showSentimentResult = false;
            } else if (source === 'preview') {
                this.showPreviewResult = false;
            }

            // Özel mod kullanılıyorsa ve otomatik başlatıldıysa, kapat
            if (this.mode !== 'menu' && this.autoRun) {
                this.$emit("close");
            }
        },
    }
};
</script>

<style lang="scss" scoped>
.mail-ai-assistant {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    font-family: 'Roboto', sans-serif;
    overflow: hidden;
    margin: 20px 0px;

    .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.95);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10;
        backdrop-filter: blur(5px);

        .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            max-width: 80%;

            .ai-brain {
                margin-bottom: 20px;

                i {
                    font-size: 60px;
                    color: #6366f1;
                    animation: pulse 1.5s infinite;
                }
            }

            .loading-text {
                font-size: 22px;
                font-weight: 600;
                color: #1f2937;
                margin-bottom: 8px;
            }

            .loading-description {
                font-size: 16px;
                color: #6b7280;
                margin-bottom: 24px;
            }

            .loading-dots {
                display: flex;
                gap: 8px;

                span {
                    width: 12px;
                    height: 12px;
                    background-color: #6366f1;
                    border-radius: 50%;
                    display: inline-block;
                    animation: loadingDots 1.4s infinite ease-in-out;

                    &:nth-child(1) {
                        animation-delay: 0s;
                    }

                    &:nth-child(2) {
                        animation-delay: 0.2s;
                    }

                    &:nth-child(3) {
                        animation-delay: 0.4s;
                    }
                }
            }
        }
    }

    .assistant-dashboard {
        padding: 24px;
        display: flex;
        flex-direction: column;
        height: 100%;

        .ai-assistant-header {
            display: flex;
            align-items: center;
            margin-bottom: 32px;

            .ai-logo {
                display: flex;
                justify-content: center;
                align-items: center;
                width: 60px;
                height: 60px;
                background: linear-gradient(135deg, #6366f1, #8b5cf6);
                border-radius: 16px;
                margin-right: 16px;
                box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.3);

                i {
                    font-size: 32px;
                    color: white;
                }
            }

            .ai-title-container {
                h2 {
                    margin: 0 0 8px 0;
                    font-size: 24px;
                    font-weight: 700;
                    color: #111827;
                }

                .ai-description {
                    margin: 0;
                    font-size: 16px;
                    color: #6b7280;
                }
            }
        }

        .feature-cards {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: 20px;
            overflow-y: auto;
            padding-right: 8px;

            .feature-card {
                display: flex;
                align-items: flex-start;
                padding: 20px;
                background-color: white;
                border-radius: 10px;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                transition: all 0.3s ease;
                cursor: pointer;
                border: 1px solid #e5e7eb;

                &:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                    border-color: #d1d5db;
                }

                .card-icon {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 50px;
                    height: 50px;
                    border-radius: 12px;
                    margin-right: 16px;
                    background: linear-gradient(135deg, #f9fafb, #f3f4f6);

                    i {
                        font-size: 28px;
                        color: #6366f1;
                    }
                }

                .card-content {
                    flex: 1;

                    h3 {
                        margin: 0 0 8px 0;
                        font-size: 16px;
                        font-weight: 600;
                        color: #111827;
                    }

                    p {
                        margin: 0;
                        font-size: 14px;
                        color: #6b7280;
                        line-height: 1.5;
                    }
                }
            }
        }
    }

    // Alt Menü
    .sub-menu-screen {
        width: 100%;
        height: 100%;
        background-color: #f9fafb;
        display: flex;
        flex-direction: column;

        .sub-menu-header {
            display: flex;
            align-items: center;
            padding: 16px 24px;
            border-bottom: 1px solid #e5e7eb;
            background-color: white;
            width: 100%;

            .back-button {
                display: flex;
                align-items: center;
                background: none;
                border: none;
                font-size: 14px;
                color: #6366f1;
                cursor: pointer;
                padding: 8px 12px;
                border-radius: 6px;
                margin-right: 16px;
                transition: background-color 0.2s;

                &:hover {
                    background-color: #eff6ff;
                }

                i {
                    font-size: 20px;
                    margin-right: 4px;
                }
            }

            h3 {
                margin: 0;
                font-size: 18px;
                font-weight: 600;
                color: #111827;
            }
        }

        .sub-options-container {
            padding: 24px;
            overflow-y: auto;

            .sub-option-card {
                display: flex;
                align-items: center;
                padding: 16px;
                background-color: white;
                border-radius: 8px;
                margin-bottom: 12px;
                cursor: pointer;
                transition: all 0.3s ease;
                border: 1px solid #e5e7eb;

                &:hover {
                    transform: translateX(5px);
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                    border-color: #d1d5db;
                }

                .option-icon {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 40px;
                    height: 40px;
                    border-radius: 8px;
                    margin-right: 16px;
                    background-color: #eff6ff;

                    i {
                        font-size: 22px;
                        color: #6366f1;
                    }
                }

                .option-content {
                    flex: 1;

                    h4 {
                        margin: 0 0 4px 0;
                        font-size: 16px;
                        font-weight: 500;
                        color: #111827;
                    }

                    p {
                        margin: 0;
                        font-size: 14px;
                        color: #6b7280;
                    }
                }
            }
        }
    }

    // Duygu Analizi Sonuç Ekranı
    .results-screen {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        z-index: 3;

        .results-container {
            padding: 24px;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            gap: 24px;

            .sentiment-summary {
                display: flex;
                justify-content: center;
                margin-bottom: 16px;

                .sentiment-badge {
                    display: flex;
                    align-items: center;
                    padding: 12px 24px;
                    border-radius: 100px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

                    i {
                        font-size: 24px;
                        margin-right: 8px;
                    }

                    span {
                        font-size: 18px;
                        font-weight: 600;
                    }

                    &.pozitif {
                        background-color: #ecfdf5;
                        color: #059669;
                    }

                    &.negatif {
                        background-color: #fef2f2;
                        color: #dc2626;
                    }

                    &.nötr {
                        background-color: #f3f4f6;
                        color: #4b5563;
                    }

                    &.karışık {
                        background-color: #fffbeb;
                        color: #d97706;
                    }
                }
            }

            .result-section {
                background-color: white;
                border-radius: 10px;
                padding: 20px;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                border: 1px solid #e5e7eb;

                h4 {
                    display: flex;
                    align-items: center;
                    margin: 0 0 16px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #111827;

                    i {
                        font-size: 20px;
                        margin-right: 8px;
                        color: #6366f1;
                    }
                }

                &.emotions-section {
                    .emotions-tags {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 8px;

                        .emotion-tag {
                            background-color: #eff6ff;
                            color: #3b82f6;
                            padding: 6px 12px;
                            border-radius: 100px;
                            font-size: 14px;
                            font-weight: 500;
                        }
                    }
                }

                &.explanation-section {
                    p {
                        margin: 0;
                        font-size: 15px;
                        line-height: 1.6;
                        color: #4b5563;
                    }
                }

                &.recommendations-section {
                    .recommendations-list {
                        margin: 0;
                        padding-left: 20px;

                        li {
                            color: #4b5563;
                            margin-bottom: 8px;
                            line-height: 1.5;
                            font-size: 15px;
                        }
                    }
                }
            }
        }
    }

    // Önizleme Ekranı
    .preview-screen {
        width: 100%;
        height: 100%;
        background-color: #f9fafb;
        display: flex;
        flex-direction: column;

        .preview-header {
            display: flex;
            align-items: center;
            padding: 16px 24px;
            border-bottom: 1px solid #e5e7eb;
            background-color: white;
            width: 100%;

            .back-button {
                display: flex;
                align-items: center;
                background: none;
                border: none;
                font-size: 14px;
                color: #6366f1;
                cursor: pointer;
                padding: 8px 12px;
                border-radius: 6px;
                margin-right: 16px;
                transition: background-color 0.2s;

                &:hover {
                    background-color: #eff6ff;
                }

                i {
                    font-size: 20px;
                    margin-right: 4px;
                }
            }

            h3 {
                margin: 0;
                font-size: 18px;
                font-weight: 600;
                color: #111827;
            }
        }

        .preview-container {
            padding: 24px;
            display: flex;
            flex-direction: column;
            flex: 1;
            overflow-y: auto;

            .ai-response-box {
                background-color: white;
                border-radius: 10px;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                border: 1px solid #e5e7eb;
                margin-bottom: 24px;
                flex: 1;

                .response-header {
                    display: flex;
                    align-items: center;
                    padding: 12px 16px;
                    border-bottom: 1px solid #e5e7eb;
                    background-color: #f9fafb;
                    border-radius: 10px 10px 0 0;

                    i {
                        font-size: 20px;
                        margin-right: 8px;
                        color: #6366f1;
                    }

                    span {
                        font-size: 14px;
                        font-weight: 500;
                        color: #4b5563;
                    }
                }

                .response-content {
                    padding: 20px;
                    max-height: calc(100vh - 240px);
                    overflow-y: auto;

                    div {
                        white-space: pre-wrap;
                        word-break: break-word;
                        font-size: 15px;
                        line-height: 1.6;
                        color: #374151;

                        h3 {
                            margin-top: 0;
                            margin-bottom: 16px;
                            font-size: 18px;
                            font-weight: 600;
                            color: #111827;
                        }

                        p {
                            margin-bottom: 16px;
                        }

                        ul,
                        ol {
                            margin-top: 8px;
                            margin-bottom: 16px;
                            padding-left: 24px;
                        }

                        li {
                            margin-bottom: 8px;
                        }
                    }
                }
            }

            .action-buttons {
                display: flex;
                gap: 12px;
                justify-content: flex-end;

                .action-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 10px 20px;
                    border-radius: 8px;
                    border: none;
                    cursor: pointer;
                    font-weight: 500;
                    font-size: 14px;
                    transition: all 0.2s;

                    i {
                        font-size: 18px;
                        margin-right: 8px;
                    }

                    &.regenerate {
                        background-color: #f3f4f6;
                        color: #374151;
                        border: 1px solid #d1d5db;

                        &:hover {
                            background-color: #e5e7eb;
                        }
                    }

                    &.apply {
                        background-color: #6366f1;
                        color: white;

                        &:hover {
                            background-color: #4f46e5;
                        }
                    }
                }
            }
        }
    }

    // Özel İstek Ekranı
    .custom-prompt-screen {
        width: 100%;
        height: 100%;
        background-color: #f9fafb;
        display: flex;
        flex-direction: column;

        .custom-prompt-header {
            display: flex;
            align-items: center;
            padding: 16px 24px;
            border-bottom: 1px solid #e5e7eb;
            background-color: white;
            width: 100%;

            .back-button {
                display: flex;
                align-items: center;
                background: none;
                border: none;
                font-size: 14px;
                color: #6366f1;
                cursor: pointer;
                padding: 8px 12px;
                border-radius: 6px;
                margin-right: 16px;
                transition: background-color 0.2s;

                &:hover {
                    background-color: #eff6ff;
                }

                i {
                    font-size: 20px;
                    margin-right: 4px;
                }
            }

            h3 {
                margin: 0;
                font-size: 18px;
                font-weight: 600;
                color: #111827;
            }
        }

        .custom-prompt-container {
            padding: 24px;
            display: flex;
            flex-direction: column;
            flex: 1;
            overflow-y: auto;
            gap: 10px;

            textarea {
                background-color: white;
                border-radius: 8px;
                padding: 12px;
                border: 1px solid #e5e7eb;
                margin-bottom: 16px;
                font-size: 15px;
                color: #374151;
            }

            .action-button {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 10px 20px;
                border-radius: 8px;
                border: none;
                cursor: pointer;
                font-weight: 500;
                font-size: 14px;
                transition: all 0.2s;

                i {
                    font-size: 18px;
                    margin-right: 8px;
                }

                &.apply {
                    background-color: #6366f1;
                    color: white;

                    &:hover {
                        background-color: #4f46e5;
                    }
                }
            }
        }
    }
}

@keyframes pulse {
    0% {
        opacity: 1;
    }

    50% {
        opacity: 0.6;
    }

    100% {
        opacity: 1;
    }
}

@keyframes loadingDots {

    0%,
    80%,
    100% {
        transform: scale(0);
    }

    40% {
        transform: scale(1);
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px dashed #d1d5db;
    width: 100%;

    .empty-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 70px;
        height: 70px;
        border-radius: 50%;
        background-color: #f3f4f6;
        margin-bottom: 20px;

        i {
            font-size: 36px;
            color: #9ca3af;
        }
    }

    h3 {
        font-size: 18px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 10px;
    }

    p {
        font-size: 15px;
        color: #6b7280;
        text-align: center;
        max-width: 80%;
    }
}

.limited-access-banner {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 12px 16px;
    background-color: #fffbeb;
    border: 1px solid #fef3c7;
    border-radius: 8px;
    margin-bottom: 16px;

    i {
        font-size: 20px;
        color: #d97706;
        margin-right: 12px;
    }

    p {
        margin: 0;
        font-size: 14px;
        color: #92400e;
    }
}
</style>
